import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { collection, getDocs, addDoc, deleteDoc, doc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { HiOutlineTrash, HiPlus } from 'react-icons/hi2';
import { IoSendOutline } from 'react-icons/io5';
import useAdminAuth from '../../../hooks/useAdminAuth';
import { friendlyTimestamp } from '../../../utils/dateFormat';

const AdminMessagesPage = () => {
  const { canSendMessage, isReadOnly, isTeacher, admin } = useAdminAuth();
  const [messages, setMessages] = useState([]);
  const [users, setUsers] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ title: '', content: '', targetRole: 'all' });
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [filter, setFilter] = useState('all');

  const load = async () => {
    const [msgSnap, userSnap] = await Promise.all([
      getDocs(collection(db, 'adminMessages')),
      getDocs(collection(db, 'users')),
    ]);
    const msgs = msgSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
    msgs.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
    setMessages(msgs);
    setUsers(userSnap.docs.map((d) => ({ uid: d.id, ...d.data() })).filter((u) => u.role !== 'admin'));
    setLoaded(true);
  };

  useEffect(() => { load(); }, []);

  // 역할별 사용자 목록
  const roleUsers = form.targetRole === 'all' ? [] : users.filter((u) => u.role === form.targetRole);

  // 역할 변경 시 전체 선택 초기화
  const handleRoleChange = (role) => {
    setForm({ ...form, targetRole: role });
    if (role === 'all') {
      setSelectedUserIds([]);
    } else {
      const filtered = users.filter((u) => u.role === role);
      setSelectedUserIds(filtered.map((u) => u.uid));
    }
  };

  // 전체 선택/해제 토글
  const toggleSelectAll = () => {
    if (selectedUserIds.length === roleUsers.length) {
      setSelectedUserIds([]);
    } else {
      setSelectedUserIds(roleUsers.map((u) => u.uid));
    }
  };

  // 개별 선택 토글
  const toggleUser = (uid) => {
    if (selectedUserIds.includes(uid)) {
      setSelectedUserIds(selectedUserIds.filter((id) => id !== uid));
    } else {
      setSelectedUserIds([...selectedUserIds, uid]);
    }
  };

  const handleSend = async () => {
    if (!form.title || !form.content) return;
    if (form.targetRole !== 'all' && selectedUserIds.length === 0) return;

    const allTargetIds = form.targetRole === 'all' ? users.map((u) => u.uid) : selectedUserIds;
    const targetNames = allTargetIds.map((uid) => users.find((u) => u.uid === uid)?.name || uid);
    const isAllInRole = form.targetRole !== 'all' && selectedUserIds.length === roleUsers.length;

    await addDoc(collection(db, 'adminMessages'), {
      title: form.title,
      content: form.content,
      targetRole: form.targetRole,
      targetUserIds: allTargetIds,
      targetUserNames: targetNames,
      isAllInRole,
      senderId: admin?.uid || null,
      senderName: admin?.name || '관리자',
      createdAt: serverTimestamp(),
    });
    setShowModal(false);
    setForm({ title: '', content: '', targetRole: 'all' });
    setSelectedUserIds([]);
    load();
  };

  const handleDelete = async (msg) => {
    if (!window.confirm(`'${msg.title}' 메시지를 삭제하시겠습니까?`)) return;
    await deleteDoc(doc(db, 'adminMessages', msg.id));
    load();
  };

  const openModal = () => {
    setForm({ title: '', content: '', targetRole: 'all' });
    setSelectedUserIds([]);
    setShowModal(true);
  };

  const filtered = filter === 'all' ? messages : messages.filter((m) => m.targetRole === filter);

  const ROLE_OPTIONS = [
    { key: 'all', label: '전체' },
    { key: 'teacher', label: '강사' },
    { key: 'student', label: '학생' },
    { key: 'assistant', label: '조교' },
    { key: 'parent', label: '학부모' },
  ];

  const TARGET_LABELS = { all: '전체', teacher: '강사', student: '학생', assistant: '조교', parent: '학부모' };
  const TARGET_COLORS = { all: '#4A7CFE', teacher: '#4A7CFE', student: '#22C55E', assistant: '#F59E0B', parent: '#A855F7' };

  // 수신자 표시 텍스트
  const getRecipientText = (msg) => {
    if (msg.targetUserNames?.length > 0) {
      const names = msg.targetUserNames;
      return `${names.join(', ')}`;
    }
    if (msg.targetRole === 'all') return '전체';
    return TARGET_LABELS[msg.targetRole] || '전체';
  };

  return (
    <Container>
      <PageHeader>
        <div>
          <PageTitle>메시지 관리</PageTitle>
          <PageDesc>전체 {messages.length}건의 메시지</PageDesc>
        </div>
        {canSendMessage && (
          <AddButton onClick={openModal}>
            <HiPlus size={16} /> 새 공지 발송
          </AddButton>
        )}
      </PageHeader>

      <FilterGroup>
        {ROLE_OPTIONS.map((r) => (
          <FilterBtn key={r.key} $active={filter === r.key} onClick={() => setFilter(r.key)}>
            {r.label}
          </FilterBtn>
        ))}
      </FilterGroup>

      <MessageList>
        {!loaded ? null : filtered.length === 0 ? (
          <Empty>메시지가 없습니다</Empty>
        ) : (
          filtered.map((msg) => (
            <MessageCard key={msg.id}>
              <MessageTop>
                <TargetBadge $color={TARGET_COLORS[msg.targetRole]}>
                  {TARGET_LABELS[msg.targetRole] || '전체'}
                </TargetBadge>
                <RecipientText>{getRecipientText(msg)}</RecipientText>
                <MessageDate>
                  {msg.createdAt ? friendlyTimestamp(msg.createdAt) : '-'}
                </MessageDate>
                {!isReadOnly && (
                  <DeleteBtn onClick={() => handleDelete(msg)}>
                    <HiOutlineTrash size={16} />
                  </DeleteBtn>
                )}
              </MessageTop>
              <MessageTitle>{msg.title}</MessageTitle>
              <MessageContent>{msg.content}</MessageContent>
              <MessageSender>{msg.senderName}</MessageSender>
            </MessageCard>
          ))
        )}
      </MessageList>

      {showModal && (
        <Overlay onClick={() => setShowModal(false)}>
          <Modal onClick={(e) => e.stopPropagation()}>
            <ModalTitle>공지 발송</ModalTitle>

            <Label>대상 그룹</Label>
            <RoleSelect>
              {(isTeacher
                ? [{ key: 'student', label: '학생' }]
                : ROLE_OPTIONS
              ).map((r) => (
                <RoleBtn key={r.key} $active={form.targetRole === r.key} onClick={() => handleRoleChange(r.key)}>
                  {r.label}
                </RoleBtn>
              ))}
            </RoleSelect>

            {/* 개별 사용자 선택 (전체가 아닐 때) */}
            {form.targetRole !== 'all' && roleUsers.length > 0 && (
              <UserSelectArea>
                <UserSelectHeader>
                  <SelectAllRow onClick={toggleSelectAll}>
                    <Checkbox $checked={selectedUserIds.length === roleUsers.length} />
                    <SelectAllLabel>전체 선택 ({roleUsers.length}명)</SelectAllLabel>
                  </SelectAllRow>
                  <SelectedCount>{selectedUserIds.length}명 선택됨</SelectedCount>
                </UserSelectHeader>
                <UserList>
                  {roleUsers.map((u) => (
                    <UserItem key={u.uid} onClick={() => toggleUser(u.uid)}>
                      <Checkbox $checked={selectedUserIds.includes(u.uid)} />
                      <UserItemName>{u.name}</UserItemName>
                      {u.school && <UserItemMeta>{u.school}</UserItemMeta>}
                      {u.grade && <UserItemBadge>{u.grade}</UserItemBadge>}
                    </UserItem>
                  ))}
                </UserList>
              </UserSelectArea>
            )}
            {form.targetRole !== 'all' && roleUsers.length === 0 && (
              <EmptyUsers>해당 역할의 사용자가 없습니다</EmptyUsers>
            )}

            <Label>제목</Label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="" />
            <Label>내용</Label>
            <Textarea value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} placeholder="" rows={5} />
            <UserCount>
              발송 대상: {form.targetRole === 'all' ? `전체 ${users.length}명` : `${selectedUserIds.length}명`}
            </UserCount>
            <ModalActions>
              <CancelBtn onClick={() => setShowModal(false)}>취소</CancelBtn>
              <SendBtn onClick={handleSend} disabled={!form.title || !form.content || (form.targetRole !== 'all' && selectedUserIds.length === 0)}>
                <IoSendOutline size={16} /> 발송
              </SendBtn>
            </ModalActions>
          </Modal>
        </Overlay>
      )}
    </Container>
  );
};

export default AdminMessagesPage;

const Container = styled.div``;
const PageHeader = styled.div`display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:20px;`;
const PageTitle = styled.h1`font-size:22px;font-weight:700;color:#111827;`;
const PageDesc = styled.p`font-size:13px;color:#9CA3AF;margin-top:2px;`;
const AddButton = styled.button`display:flex;align-items:center;gap:6px;padding:8px 16px;border-radius:8px;background:#4A7CFE;color:white;font-size:13px;font-weight:600;&:hover{background:#3B6DE8;}`;

const FilterGroup = styled.div`display:flex;gap:4px;margin-bottom:16px;`;
const FilterBtn = styled.button`
  padding:7px 14px;border-radius:8px;font-size:13px;
  font-weight:${({ $active }) => $active ? 600 : 400};
  color:${({ $active }) => $active ? '#4A7CFE' : '#6B7280'};
  background:${({ $active }) => $active ? '#EEF2FF' : 'transparent'};
  border:1px solid ${({ $active }) => $active ? '#C7D2FE' : 'transparent'};
  &:hover{background:${({ $active }) => $active ? '#EEF2FF' : '#F9FAFB'};}
`;

const MessageList = styled.div`display:flex;flex-direction:column;gap:12px;`;
const MessageCard = styled.div`background:white;border-radius:12px;border:1px solid #EAEDF3;padding:20px;`;
const MessageTop = styled.div`display:flex;align-items:center;gap:10px;margin-bottom:10px;`;
const TargetBadge = styled.span`
  padding:3px 10px;border-radius:12px;font-size:12px;font-weight:600;
  background:${({ $color }) => $color}15;color:${({ $color }) => $color};
`;
const RecipientText = styled.span`font-size:13px;font-weight:600;color:#374151;`;
const MessageDate = styled.span`font-size:12px;color:#9CA3AF;flex:1;text-align:right;`;
const DeleteBtn = styled.button`padding:4px;border-radius:4px;color:#D1D5DB;&:hover{color:#EF4444;background:#FEF2F2;}`;
const MessageTitle = styled.h3`font-size:16px;font-weight:700;color:#111827;margin-bottom:6px;`;
const MessageContent = styled.p`font-size:14px;color:#6B7280;line-height:1.6;white-space:pre-wrap;`;
const MessageSender = styled.p`font-size:12px;color:#D1D5DB;margin-top:10px;`;
const Empty = styled.p`padding:40px;text-align:center;color:#9CA3AF;font-size:14px;background:white;border-radius:12px;border:1px solid #EAEDF3;`;

// Modal
const Overlay = styled.div`position:fixed;inset:0;background:rgba(0,0,0,0.4);display:flex;align-items:center;justify-content:center;z-index:50;`;
const Modal = styled.div`background:white;border-radius:16px;padding:24px;width:520px;max-width:90vw;max-height:90vh;overflow-y:auto;`;
const ModalTitle = styled.h2`font-size:18px;font-weight:700;color:#1E3A5F;margin-bottom:16px;`;
const Label = styled.label`display:block;font-size:13px;font-weight:600;color:#374151;margin-bottom:6px;margin-top:14px;`;
const Input = styled.input`width:100%;padding:10px 12px;border:1px solid #E5E7EB;border-radius:8px;font-size:14px;box-sizing:border-box;&:focus{border-color:#4A7CFE;outline:none;}`;
const Textarea = styled.textarea`width:100%;padding:10px 12px;border:1px solid #E5E7EB;border-radius:8px;font-size:14px;resize:vertical;box-sizing:border-box;font-family:inherit;&:focus{border-color:#4A7CFE;outline:none;}`;
const UserCount = styled.p`font-size:13px;color:#4A7CFE;font-weight:600;margin-top:8px;`;
const ModalActions = styled.div`display:flex;gap:8px;justify-content:flex-end;margin-top:20px;`;
const CancelBtn = styled.button`padding:10px 20px;border-radius:8px;border:1px solid #E5E7EB;font-size:14px;color:#374151;&:hover{background:#F9FAFB;}`;
const SendBtn = styled.button`display:flex;align-items:center;gap:6px;padding:10px 20px;border-radius:8px;background:#4A7CFE;color:white;font-size:14px;font-weight:600;&:hover{background:#3B6DE8;}&:disabled{opacity:0.5;}`;

// Role select
const RoleSelect = styled.div`display:flex;gap:4px;flex-wrap:wrap;`;
const RoleBtn = styled.button`
  padding:8px 16px;border-radius:8px;font-size:13px;font-weight:600;transition:all 0.15s;
  ${({ $active }) => $active
    ? 'background:#4A7CFE;color:white;'
    : 'background:#F3F4F6;color:#6B7280;&:hover{background:#E5E7EB;}'}
`;

// User select
const UserSelectArea = styled.div`margin-top:12px;border:1px solid #E5E7EB;border-radius:10px;overflow:hidden;`;
const UserSelectHeader = styled.div`display:flex;justify-content:space-between;align-items:center;padding:10px 14px;background:#FAFBFC;border-bottom:1px solid #F3F4F6;`;
const SelectAllRow = styled.button`display:flex;align-items:center;gap:8px;`;
const SelectAllLabel = styled.span`font-size:13px;font-weight:600;color:#374151;`;
const SelectedCount = styled.span`font-size:12px;font-weight:600;color:#4A7CFE;`;
const Checkbox = styled.div`
  width:18px;height:18px;border-radius:4px;border:2px solid ${({ $checked }) => $checked ? '#4A7CFE' : '#D1D5DB'};
  background:${({ $checked }) => $checked ? '#4A7CFE' : 'white'};
  display:flex;align-items:center;justify-content:center;flex-shrink:0;transition:all 0.15s;
  &::after{content:'${({ $checked }) => $checked ? '✓' : ''}';color:white;font-size:11px;font-weight:700;}
`;
const UserList = styled.div`max-height:200px;overflow-y:auto;`;
const UserItem = styled.button`
  width:100%;display:flex;align-items:center;gap:10px;padding:10px 14px;
  border-bottom:1px solid #F3F4F6;text-align:left;
  &:last-child{border:none;}&:hover{background:#F0F7FF;}
`;
const UserItemName = styled.span`font-size:14px;font-weight:600;color:#111827;`;
const UserItemMeta = styled.span`font-size:12px;color:#9CA3AF;`;
const UserItemBadge = styled.span`font-size:11px;color:#6B7280;background:#F3F4F6;padding:1px 6px;border-radius:4px;`;
const EmptyUsers = styled.p`margin-top:12px;font-size:13px;color:#9CA3AF;`;
